import { orderSourceLabel } from '@/utils/orderSource';
/**
 * Order Sheet helper — builds a clean, legible 8.5x11 portrait "order form"
 * ENTIRELY from order data the app already stores.
 *
 * Two consumers share the same markup:
 *  - printOrderSheet(order, shop)      → opens the sheet in a window as a
 *    read-only viewer with its own "Print" button. It does NOT auto-print;
 *    the user prints on demand.
 *  - buildOrderSheetHtml(order, shop)  → returns the same page as a string,
 *    used as the inline Design-view preview (via an <iframe srcDoc>) for orders
 *    that have no scannable source document (manual, phone, walk-in, Stripe).
 *    In that mode the auto-print script is omitted so previewing never prints.
 *
 * Its job is to give any order — especially app-origin ones that have no
 * printable original — a board card without leaving the app.
 *
 * Tier 1 scope: no cost breakdown (subtotal/delivery/tax) and no sender/buyer
 * contact — those aren't stored yet and are a later decision. Sender is shown
 * as the network source + the extracted sending-florist name, both of which
 * are already on the order.
 */
import { toast } from 'sonner';
import { API } from '@/config/constants';
import { escapeHtml as esc } from '@/utils/htmlEscape';

/**
 * Build the self-contained order-sheet HTML document.
 * @param {object} order  the order record
 * @param {object} shop   shop branding (name/address/logo_id)
 * @param {{autoPrint?: boolean, includeOrderNumber?: boolean}} opts  autoPrint adds a window.print() script
 *        (used by the print window); omit it for the inline preview.
 * @returns {string} a complete <!DOCTYPE html> document
 */
export function buildOrderSheetHtml(order, shop, { autoPrint = false, toolbar = false, brandingFallback = false, includeOrderNumber = true } = {}) {
  if (!order) return '';

  const shopName = esc((shop && shop.name) || 'Las Vegas Bouquet');
  const shopAddress = esc((shop && shop.address) || '');
  const logoSrc = shop && shop.logo_id
    ? `${API}/public/shop-logo?v=${encodeURIComponent(shop.logo_id)}`
    : null;

  const ion = esc(order.internal_order_number || '—');
  // Same short-number convention as backend/utils/document_stamp.py. Do not
  // accept a partially numeric suffix or fall back to the external order ID.
  const suffix = String(order.internal_order_number || '').split('-').pop();
  const sequence = /^\d+$/.test(suffix) ? Number(suffix) : NaN;
  const stamp = includeOrderNumber && Number.isSafeInteger(sequence)
    ? `<svg class="order-number-stamp" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><text x="100%" y="100%" text-anchor="end">${sequence}</text></svg>` : '';
  const source = esc(orderSourceLabel(order, 'Manual'));
  const orderNumber = esc(order.order_number || '');
  const sendingFlorist = esc(order.subsource_extracted || '');
  const deliveryDate = esc(order.delivery_date || '');
  const targetTime = esc(order.delivery_time_requirement || '');
  const acceptedDate = esc(order.order_accepted_date || '');

  const recipientName = esc(order.recipient_name || '');
  const addr1 = esc(order.recipient_street_address || '');
  const addr2 = esc(order.recipient_street_address_2 || '');
  const cityStateZip = esc(
    [order.recipient_city, order.recipient_state, order.recipient_zip_code]
      .filter(Boolean).join(', ')
  );
  const phone = esc(order.recipient_phone || '');

  const productCode = esc(order.product_code || '');
  const productName = esc(order.product_name || '');
  const size = esc(order.size || '');
  const productDescription = esc(order.product_description || '');
  const cardMessage = esc(order.card_message || '');
  const specialInstructions = esc(order.special_instructions || '');
  const totalPrice = order.total_price != null ? `$${Number(order.total_price).toFixed(2)}` : '';

  // A labeled field, rendered only when it has a value.
  const field = (label, value) => value
    ? `<div class="field"><span class="label">${label}</span><span class="value">${value}</span></div>`
    : '';

  const autoPrintScript = autoPrint ? `<script>
  (function(){
    function go(){ try { window.focus(); window.print(); } catch(e){} }
    var img = document.querySelector('.shop-logo');
    if (!img) { setTimeout(go, 150); return; }
    if (img.complete) { setTimeout(go, 100); return; }
    img.addEventListener('load',  function(){ setTimeout(go, 100); });
    img.addEventListener('error', function(){ setTimeout(go, 100); });
    setTimeout(go, 2000); // fallback if the logo stalls
  })();
</script>` : '';

  // Screen-only toolbar (used by the window viewer) with an on-demand Print
  // button; hidden when actually printing.
  const toolbarHtml = toolbar ? `<div class="print-toolbar no-print">
    ${brandingFallback ? '<span style="color:white;margin-right:12px">Shop branding unavailable; using default branding.</span>' : ''}
    <button type="button" onclick="window.print()">Print</button>
  </div>` : '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Order ${ion}</title>
<style>
  @page { size: letter portrait; margin: 0.5in; }
  html, body { margin: 0; padding: 0; background: #fff; color: #111;
    font-family: -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; }
  .sheet { position: relative; min-height: 10in; box-sizing: border-box; padding: 18px 20px; }
  /* Letter minus the existing half-inch margins: anchor once to page one,
     not the bottom of a long sheet or a fixed element repeated on every page. */
  .order-number-stamp { position: absolute; top: calc(10in - 99pt); right: 0;
    width: 100%; height: 99pt; overflow: visible; pointer-events: none; z-index: 1; }
  .order-number-stamp text { font-family: Helvetica, Arial, sans-serif;
    font-size: 99pt; font-weight: 700; fill: #333; opacity: 0.5; }
  .header { display: flex; align-items: center; justify-content: space-between;
    border-bottom: 2px solid #111; padding-bottom: 10px; margin-bottom: 14px; }
  .shop { display: flex; align-items: center; gap: 10px; }
  .shop-logo { max-height: 46px; max-width: 160px; object-fit: contain; }
  .shop-name { font-size: 15px; font-weight: 800; letter-spacing: 0.5px; text-transform: uppercase; }
  .shop-address { font-size: 10px; color: #555; }
  .board-number { text-align: right; }
  .board-number .bn-label { font-size: 9px; text-transform: uppercase; letter-spacing: 1px; color: #666; }
  .board-number .bn-value { font-size: 30px; font-weight: 800; font-family: ui-monospace, Menlo, monospace; line-height: 1; }
  .meta { display: flex; flex-wrap: wrap; gap: 6px 26px; margin-bottom: 16px; }
  .meta .field { margin-bottom: 0; }
  .section { border: 1px solid #ddd; border-radius: 8px; margin-bottom: 12px; overflow: hidden; page-break-inside: avoid; }
  .section-title { background: #f1f5f9; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: #475569; padding: 5px 12px; }
  .section-body { padding: 10px 12px; }
  .field { margin-bottom: 6px; }
  .field:last-child { margin-bottom: 0; }
  .label { display: inline-block; min-width: 130px; font-size: 11px; font-weight: 700; color: #444; vertical-align: top; }
  .value { font-size: 13px; color: #111; white-space: pre-wrap; }
  .recipient-name { font-size: 20px; font-weight: 800; margin-bottom: 4px; }
  .big-total { font-size: 18px; font-weight: 800; }
  .msg { font-style: italic; }
  .print-toolbar { position: sticky; top: 0; z-index: 10; background: #1f2733; text-align: right; padding: 8px 12px; }
  .print-toolbar button { font: 600 13px -apple-system, 'Segoe UI', Roboto, sans-serif;
    background: #fff; color: #1f2733; border: 0; border-radius: 6px; padding: 6px 16px; cursor: pointer; }
  @media print { .no-print { display: none !important; } }
</style>
</head>
<body>
  ${toolbarHtml}
  <div class="sheet">
    ${stamp}
    <div class="header">
      <div class="shop">
        ${logoSrc ? `<img class="shop-logo" src="${logoSrc}" alt="${shopName}" crossorigin="anonymous" />` : ''}
        <div>
          <div class="shop-name">${shopName}</div>
          ${shopAddress ? `<div class="shop-address">${shopAddress}</div>` : ''}
        </div>
      </div>
      <div class="board-number">
        <div class="bn-label">Order</div>
        <div class="bn-value">${ion}</div>
      </div>
    </div>

    <div class="meta">
      ${field('Source', source)}
      ${field('Order #', orderNumber)}
      ${field('Sending Florist', sendingFlorist)}
      ${field('Delivery Date', deliveryDate)}
      ${field('Target Time', targetTime)}
      ${field('Accepted', acceptedDate)}
    </div>

    <div class="section">
      <div class="section-title">Recipient</div>
      <div class="section-body">
        <div class="recipient-name">${recipientName || '&nbsp;'}</div>
        <div class="value">${addr1}${addr2 ? '<br/>' + addr2 : ''}${cityStateZip ? '<br/>' + cityStateZip : ''}</div>
        ${phone ? `<div class="value" style="margin-top:4px">${phone}</div>` : ''}
      </div>
    </div>

    <div class="section">
      <div class="section-title">Arrangement</div>
      <div class="section-body">
        ${field('Product Code', productCode)}
        ${field('Product', productName)}
        ${field('Size', size)}
        ${productDescription ? `<div class="field"><span class="label">Description</span><span class="value">${productDescription}</span></div>` : ''}
      </div>
    </div>

    ${cardMessage ? `<div class="section">
      <div class="section-title">Card Message</div>
      <div class="section-body"><div class="value msg">"${cardMessage}"</div></div>
    </div>` : ''}

    ${specialInstructions ? `<div class="section">
      <div class="section-title">Special Instructions</div>
      <div class="section-body"><div class="value">${specialInstructions}</div></div>
    </div>` : ''}

    <div class="section">
      <div class="section-title">Order</div>
      <div class="section-body">
        <div class="field"><span class="label">Total</span><span class="value big-total">${totalPrice || '—'}</span></div>
      </div>
    </div>
  </div>
${autoPrintScript}
</body>
</html>`;
}

export function printOrderSheet(order, shop, { popup, brandingFallback = false, includeOrderNumber = true } = {}) {
  if (!order) return;

  const html = buildOrderSheetHtml(order, shop, { toolbar: true, brandingFallback, includeOrderNumber });

  const w = popup || window.open('', '_blank', 'width=800,height=1000');
  if (!w) {
    toast.error('Pop-ups blocked — allow pop-ups to view the order sheet');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
